import type { ActionPlatform } from '../types/breaks';

interface PlatformGlyphProps {
  platform: ActionPlatform;
  className?: string;
}

export function PlatformGlyph({ platform, className }: PlatformGlyphProps) {
  const Glyph = GLYPHS[platform];
  return <Glyph className={className} />;
}

const Slack = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M5.04 15.16a2.07 2.07 0 11-2.07-2.07h2.07v2.07zm1.04 0c0-1.14.93-2.07 2.07-2.07s2.07.93 2.07 2.07v5.18a2.07 2.07 0 01-4.14 0v-5.18zM8.15 5.04a2.07 2.07 0 11-2.07 2.07V5.04h2.07zm0 1.04c1.14 0 2.07.93 2.07 2.07s-.93 2.07-2.07 2.07H2.97a2.07 2.07 0 010-4.14h5.18zm10.12 2.07a2.07 2.07 0 112.07 2.07h-2.07V8.15zm-1.04 0c0 1.14-.93 2.07-2.07 2.07a2.07 2.07 0 01-2.07-2.07V2.97a2.07 2.07 0 014.14 0v5.18zm-2.07 10.12a2.07 2.07 0 112.07-2.07v2.07h-2.07zm0-1.04c-1.14 0-2.07-.93-2.07-2.07s.93-2.07 2.07-2.07h5.18a2.07 2.07 0 010 4.14h-5.18z" />
  </svg>
);

const Jira = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M11.53 2c0 2.4 1.97 4.35 4.35 4.35h1.78v1.7a4.35 4.35 0 004.34 4.34V2.84a.84.84 0 00-.84-.84h-9.63zM6.78 6.8a4.35 4.35 0 004.34 4.34h1.78v1.72a4.35 4.35 0 004.34 4.34V7.65a.84.84 0 00-.83-.84H6.78zM2.05 11.6a4.35 4.35 0 004.34 4.34h1.78v1.71A4.35 4.35 0 0012.5 22V12.45a.84.84 0 00-.83-.84H2.05z" />
  </svg>
);

const Confluence = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M.87 18.22c-.25.4-.53.87-.76 1.24a.76.76 0 00.26 1.04l4.93 3.04a.76.76 0 001.05-.26c.2-.34.46-.78.74-1.24 1.97-3.26 3.96-2.86 7.55-1.15l4.9 2.32a.76.76 0 001-.36l2.35-5.32a.76.76 0 00-.38-1c-1.03-.49-3.07-1.45-4.92-2.34-6.62-3.22-12.24-3.01-16.72 4.03zM23.13 5.78c.25-.4.53-.87.76-1.24a.76.76 0 00-.26-1.04L18.7.46a.76.76 0 00-1.05.26c-.2.34-.46.78-.74 1.24-1.97 3.26-3.96 2.86-7.55 1.15L4.46.79a.76.76 0 00-1 .36L1.1 6.47a.76.76 0 00.38 1c1.03.49 3.07 1.45 4.92 2.34 6.62 3.22 12.24 3.01 16.72-4.03z" />
  </svg>
);

const DynamicsCrm = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M3 3.8L13 2v9L3 11.2V3.8zm0 8.4L13 13v9L3 20.2v-8zm11 .9l7 .7v6.4l-7 .8V13.1zM14 2.6L21 3.8v6.6l-7-.5V2.6z" />
  </svg>
);

const GitHub = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M12 .5a11.5 11.5 0 00-3.64 22.41c.58.1.79-.25.79-.56v-2c-3.2.7-3.88-1.54-3.88-1.54-.53-1.34-1.3-1.7-1.3-1.7-1.05-.72.08-.71.08-.71 1.16.08 1.78 1.2 1.78 1.2 1.03 1.77 2.7 1.26 3.36.96.1-.75.4-1.26.73-1.55-2.55-.29-5.23-1.28-5.23-5.7 0-1.26.45-2.29 1.2-3.1-.13-.3-.52-1.48.1-3.08 0 0 .97-.31 3.18 1.18a11 11 0 015.8 0c2.2-1.5 3.17-1.18 3.17-1.18.63 1.6.24 2.78.12 3.08.75.81 1.2 1.84 1.2 3.1 0 4.43-2.69 5.41-5.25 5.69.41.36.78 1.05.78 2.12v3.15c0 .31.2.67.8.56A11.5 11.5 0 0012 .5z" />
  </svg>
);

const PagerDuty = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M5 2h6.6c3.4 0 5.9 2.2 5.9 5.6S15 13.3 11.6 13.3H8.4V22H5V2zm3.4 3v5.3h2.9c1.6 0 2.7-1 2.7-2.65S12.9 5 11.3 5H8.4z" />
  </svg>
);

// BMC Helix - the ITSM product. A double-helix mark with service rungs.
const BmcHelix = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M7 3c0 4 10 5 10 9s-10 5-10 9" />
    <path d="M17 3c0 4-10 5-10 9s10 5 10 9" />
    <path d="M8.4 6h7.2M6.6 12h10.8M8.4 18h7.2" />
  </svg>
);

const Calendar = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18M12 14v4M10 16h4" />
  </svg>
);

const Intera = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
  </svg>
);

const GLYPHS: Record<
  ActionPlatform,
  React.ComponentType<{ className?: string }>
> = {
  slack: Slack,
  jira: Jira,
  confluence: Confluence,
  'dynamics-crm': DynamicsCrm,
  github: GitHub,
  pagerduty: PagerDuty,
  'bmc-helix': BmcHelix,
  calendar: Calendar,
  intera: Intera,
};
